import { useEffect, useRef, useState } from 'react'

/**
 * Where the reader's selection is, in coordinates something can be drawn at.
 *
 * The document the engine paints is not the document the browser selects in: a
 * drag on the page is mirrored into a hidden measurement copy parked at
 * x = -9999, so the DOM selection's own rects are nowhere near the text the
 * reader highlighted. The engine paints the selection itself instead, one rect
 * per line at real coordinates, and those rects are the only honest answer to
 * "where is the highlight".
 *
 * The text is asked of the engine for the same reason. That hidden copy is a
 * sibling of the page rather than a child, so anything that takes focus away
 * from it — a modal dialog focusing its own buttons — collapses the browser's
 * selection inside it while the engine's is still standing and still painted.
 */

/**
 * Marks the controls a selection puts on screen. They are pressed with the
 * selection still standing, so a press on one is not the start of a new drag and
 * must not take the selection away — which would unmount the very button being
 * pressed, before the press landed.
 */
export const SELECTION_UI_ATTRIBUTE = 'data-w3-selection-ui'

/**
 * One painted piece of the local selection — a line of text, or a whole cell
 * when the highlight runs across a table, which the engine paints under a class
 * of its own. Exported for the chat panel's selection popover, which anchors
 * to these rects for the same reason this file reads them: they are the only
 * geometry that matches the visible highlight.
 */
export const SELECTION_RECT_SELECTOR = [
  '.presentation-editor__selection-layer--local .presentation-editor__selection-rect',
  '.presentation-editor__selection-layer--local .presentation-editor__cell-selection-rect',
].join(',')

export interface SelectionAnchor {
  /** Bottom of the last selected line, relative to the viewer root. */
  bottom: number
  /** Right edge of the last selected line, relative to the viewer root. */
  right: number
  /**
   * Top of the first selected line in the scroller's *content* coordinates —
   * which is the space the comment rail lays its cards out in, and so what a
   * card for this selection is anchored to.
   */
  contentTop: number
  /** The selected text. */
  text: string
}

const sameAnchor = (
  a: SelectionAnchor | null,
  b: SelectionAnchor | null,
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.bottom === b.bottom &&
    a.right === b.right &&
    a.contentTop === b.contentTop &&
    a.text === b.text)

const readAnchor = (
  root: HTMLElement,
  scroller: HTMLElement | null,
  readText: () => string,
): SelectionAnchor | null => {
  const text = readText()
  if (!text.trim()) {
    return null
  }
  const rects = root.querySelectorAll<HTMLElement>(SELECTION_RECT_SELECTOR)
  if (rects.length === 0) {
    return null
  }
  const rootRect = root.getBoundingClientRect()
  const first = rects[0].getBoundingClientRect()
  const last = rects[rects.length - 1].getBoundingClientRect()
  const scrollerRect = scroller?.getBoundingClientRect()
  return {
    bottom: last.bottom - rootRect.top,
    right: last.right - rootRect.left,
    contentTop: scrollerRect
      ? first.top - scrollerRect.top + (scroller?.scrollTop ?? 0)
      : first.top - rootRect.top,
    text: text.trim(),
  }
}

/**
 * The reader's selection, tracked while `enabled`, and null when there is none.
 *
 * Recomputed on a frame rather than per event, and not at all while the reader is
 * still dragging: a drag fires selectionchange for every pixel of it, and each
 * answer costs a layout read taken right after the engine repainted the
 * selection. Word offers nothing until the button is released, and a button that
 * chases the cursor is one the reader has to outrun to click.
 *
 * `resetKey` forces a re-read when it changes: a host can collapse the engine's
 * selection out-of-band (see DocxViewerProps.selectionResetNonce), and that
 * dispatch repaints the page without any of the DOM events listened for here.
 */
export const useSelectionAnchor = (
  rootRef: React.RefObject<HTMLElement | null>,
  scroller: HTMLElement | null,
  enabled: boolean,
  readText: () => string,
  resetKey?: unknown,
): SelectionAnchor | null => {
  const [anchor, setAnchor] = useState<SelectionAnchor | null>(null)
  const readTextRef = useRef(readText)
  readTextRef.current = readText

  useEffect(() => {
    const root = rootRef.current
    if (!enabled || !root) {
      setAnchor(null)
      return
    }
    let frame = 0
    let dragging = false
    const update = () => {
      frame = 0
      const next = readAnchor(root, scroller, readTextRef.current)
      setAnchor((current) => (sameAnchor(current, next) ? current : next))
    }
    const schedule = () => {
      if (!frame && !dragging) {
        frame = requestAnimationFrame(update)
      }
    }
    const startDrag = (event: PointerEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest(`[${SELECTION_UI_ATTRIBUTE}]`)
      ) {
        return
      }
      dragging = true
      if (frame) {
        cancelAnimationFrame(frame)
        frame = 0
      }
      setAnchor(null)
    }
    const endDrag = () => {
      dragging = false
      schedule()
    }
    // Scrolling moves the painted rects without changing the selection, so it
    // is watched for as well — a button left behind at the old position would
    // add a comment to whatever has scrolled under it.
    document.addEventListener('selectionchange', schedule)
    document.addEventListener('pointerdown', startDrag)
    // On the window, so a drag that ends outside the document still answers.
    window.addEventListener('pointerup', endDrag)
    window.addEventListener('pointercancel', endDrag)
    scroller?.addEventListener('scroll', schedule, { passive: true })
    schedule()
    return () => {
      document.removeEventListener('selectionchange', schedule)
      document.removeEventListener('pointerdown', startDrag)
      window.removeEventListener('pointerup', endDrag)
      window.removeEventListener('pointercancel', endDrag)
      scroller?.removeEventListener('scroll', schedule)
      if (frame) {
        cancelAnimationFrame(frame)
      }
    }
  }, [rootRef, scroller, enabled, resetKey])

  return anchor
}
